import type { InferRequestType, InferResponseType } from 'hono/client';
import { useCallback } from 'react';
import { useSWRConfig } from 'swr';
import { type ApiClient, type ReadToastOptions, useFetch, useMutation } from '../api';
import type { WorkspaceListResponse, WorkspaceResponse } from './workspaces_resource';

type GetWorkspaceOwners = ApiClient['v2']['workspaces'][':workspaceId']['owners']['$get'];
type PatchWorkspace = ApiClient['v2']['workspaces'][':workspaceId']['$patch'];
type PutWorkspace = ApiClient['v2']['workspaces'][':workspaceId']['$put'];
type PostWorkspace = ApiClient['v2']['workspaces']['$post'];

export type WorkspaceOwnersResponse = InferResponseType<GetWorkspaceOwners, 200>;
export type WorkspaceOwner = WorkspaceOwnersResponse['owners'][number];

export type PatchWorkspaceRequest = InferRequestType<PatchWorkspace>['json'];
export type PutWorkspaceRequest = InferRequestType<PutWorkspace>['json'];
export type CreateWorkspaceRequest = InferRequestType<PostWorkspace>['json'];

const workspaceCacheKey = (workspaceId: number | string) => `workspaces/${workspaceId}`;

export function useWorkspace(
  workspaceId: number | null | undefined,
  toastOptions?: ReadToastOptions
) {
  return useFetch<WorkspaceResponse>(
    workspaceId ? workspaceCacheKey(workspaceId) : null,
    (client) =>
      client.v2.workspaces[':workspaceId'].$get({
        param: { workspaceId: workspaceId!.toString() },
      }),
    toastOptions
  );
}

export function useWorkspaceOwners(workspaceId: number | null | undefined) {
  return useFetch<WorkspaceOwnersResponse>(
    workspaceId ? `${workspaceCacheKey(workspaceId)}/owners` : null,
    (client) =>
      client.v2.workspaces[':workspaceId'].owners.$get({
        param: { workspaceId: workspaceId!.toString() },
      })
  );
}

export function useUpdateWorkspace(workspaceId: number) {
  const { mutate } = useSWRConfig();
  const cacheKey = workspaceCacheKey(workspaceId);
  const mutation = useMutation<WorkspaceResponse, PatchWorkspaceRequest>(
    cacheKey,
    (client, updates) =>
      client.v2.workspaces[':workspaceId'].$patch({
        param: { workspaceId: workspaceId.toString() },
        json: updates,
      })
  );

  return useCallback(
    async (updates: PatchWorkspaceRequest) => {
      const result = await mutation(updates);
      await mutate(cacheKey, result, { revalidate: false });
      return result;
    },
    [mutation, mutate, cacheKey]
  );
}

export function useReplaceWorkspace(workspaceId: number) {
  const { mutate } = useSWRConfig();
  const cacheKey = workspaceCacheKey(workspaceId);
  const mutation = useMutation<WorkspaceResponse, PutWorkspaceRequest>(
    cacheKey,
    (client, workspace) =>
      client.v2.workspaces[':workspaceId'].$put({
        param: { workspaceId: workspaceId.toString() },
        json: workspace,
      })
  );

  return useCallback(
    async (workspace: PutWorkspaceRequest) => {
      const result = await mutation(workspace);
      await mutate(cacheKey, result, { revalidate: false });
      return result;
    },
    [mutation, mutate, cacheKey]
  );
}

export function useCreateWorkspace(onError: (error?: unknown) => void = () => {}) {
  const { mutate } = useSWRConfig();
  const cacheKey = 'workspaces/me';
  const mutation = useMutation<WorkspaceResponse, CreateWorkspaceRequest>(
    cacheKey,
    (client, data) => client.v2.workspaces.$post({ json: data }),
    onError,
    { suppressToast: true }
  );

  return useCallback(
    async (data: CreateWorkspaceRequest) => {
      const result = await mutation(data);
      // Invalidate every workspaces list query (both `/workspaces` and `/workspaces/me?…`)
      void mutate(
        (key: unknown) =>
          typeof key === 'string' &&
          (key === 'workspaces' ||
            key === cacheKey ||
            key.startsWith('workspaces/me?') ||
            key.startsWith('workspaces?'))
      );
      return result;
    },
    [mutation, mutate]
  );
}

// Re-export list-level response types so existing imports keep working.
export type { WorkspaceListResponse, WorkspaceResponse };
